import { useState, useRef } from "react";

const DASHBOARD_API = process.env.REACT_APP_API_URL || "http://localhost:5050/api";

export default function AdhocUploadModal({ isOpen, onClose, onSubmit, accountName }) {
  const [name,       setName]       = useState("");
  const [file,       setFile]       = useState(null);
  const [fileLabel,  setFileLabel]  = useState("Accepts .xlsx or .csv");
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  function reset() {
    setName("");
    setFile(null);
    setFileLabel("Accepts .xlsx or .csv");
    setSubmitting(false);
    if (fileRef.current) fileRef.current.value = "";
  }

  function handleClose() {
    reset();
    onClose();
  }

  function onFileChange(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setFileLabel(`✓ ${f.name} (${(f.size / 1024).toFixed(1)} KB) ready`);
  }

  async function handleSubmit() {
    if (!name.trim()) { alert("Portfolio name is required."); return; }
    if (!file)        { alert("Please select a file to upload."); return; }
    setSubmitting(true);
    try {
      await onSubmit({ name: name.trim(), file });
      reset();
    } catch {
      setSubmitting(false);
    }
  }

  function handleOverlayClick(e) {
    if (e.target === e.currentTarget) handleClose();
  }

  return (
    <div className={`port-modal-overlay${isOpen ? " open" : ""}`} onMouseDown={handleOverlayClick}>
      <div className="port-modal">
        <div className="port-modal-hdr">
          <div>
            <div className="port-modal-title">Upload Portfolio for Analysis</div>
            <div className="port-modal-subtitle">One-time analysis — results appear in What-If</div>
          </div>
          <button className="port-modal-close" onClick={handleClose}>✕</button>
        </div>

        <div className="port-modal-body">
          <div className="port-modal-alert port-modal-alert-adhoc">
            <div className="port-modal-alert-icon port-modal-alert-icon-adhoc">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <div>
              <div className="port-modal-alert-title">Not tracked — one-time analysis only</div>
              <div className="port-modal-alert-sub">
                This portfolio will <strong>not</strong> affect {accountName}'s daily risk report. Use it for
                what-if scenarios or stress tests. Risk results will appear in the <strong>What-If</strong> page.
              </div>
            </div>
          </div>

          <div className="port-name-row">
            <label className="port-name-label">
              Portfolio Name <span style={{ color: "var(--dash-red)" }}>*</span>
            </label>
            <input
              className="port-name-input"
              type="text"
              placeholder="e.g. Rebalance — Q2 2026, Stress Test Scenario A…"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="port-step-row">
            <div className="port-step-num port-step-num-gold">1</div>
            <div className="port-step-body">
              <div className="port-step-title">Download the input template</div>
              <div className="port-step-sub">Same format as tracked positions — one template for both</div>
            </div>
            <a
              href={`${DASHBOARD_API}/download_file/input_template.xlsx?token=${sessionStorage.getItem("token")}`}
              className="port-step-action port-step-action-gold"
            >
              ↓ Template
            </a>
          </div>

          <div className="port-step-row">
            <div className="port-step-num port-step-num-blue">2</div>
            <div className="port-step-body">
              <div className="port-step-title">Upload portfolio file</div>
              <div className={`port-step-sub${file ? " ok" : ""}`}>{fileLabel}</div>
            </div>
            <label className="port-step-action port-step-action-blue" style={{ cursor: "pointer" }}>
              ↑ Browse
              <input
                type="file"
                accept=".xlsx,.csv"
                style={{ display: "none" }}
                ref={fileRef}
                onChange={onFileChange}
              />
            </label>
          </div>
        </div>

        <div className="port-modal-footer">
          <button className="port-btn port-btn-ghost" onClick={handleClose} disabled={submitting}>
            Cancel
          </button>
          <button
            className="port-btn port-btn-blue"
            onClick={handleSubmit}
            disabled={submitting || !file || !name.trim()}
          >
            {submitting ? "Uploading…" : "Submit for Analysis"}
          </button>
        </div>
      </div>
    </div>
  );
}
